import { Controller } from "@hotwired/stimulus"
import FirstPlanAnalysisAnimation from "../src/first_plan_analysis_animation"

// Routine setup leads into product matching. Results and completion are rendered by Rails.
export default class extends Controller {
  static targets = [
    "step", "backButton", "computed", "completion",
    "ring", "percent", "status", "carrier", "decisionError", "frequencyForm", "reminderStatus", "reminderDone"
  ]

  static values = {
    assessmentUrl: String,
    analyticsUrl: String,
    decisionUrl: String,
    nextStepUrl: String,
    todayUrl: String,
    reminderUrl: String,
    ingredientCount: Number,
    pairingCount: Number
  }

  // Entry animation. The bubble arrives first and the screen's content blocks follow about a
  // second later, long enough to register the bubble without holding the screen hostage.
  READ_PAUSE_MS = 1000
  CONTINUATION_DELAY_MS = 700
  REVEAL_STEP_MS = 70
  SCREEN_SLIDE_MS = 420

  connect() {
    this.current = 0
    this.revealed = new Set()
    this.trackedSteps = new Set()
    this.revealTimeouts = []
    this.compatibilityAnimations = []
    this.revealToken = 0
    this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.showCurrent()
  }

  disconnect() {
    this.analysisAnimation?.cancel()
    this.cancelReveal()
    this.screenAnimation?.cancel()
  }

  // ---- navigation ---------------------------------------------------------------------------

  next(event) {
    if (this.decision) return

    const key = this.currentKey

    if (key === "match_scores") return this.beginAnalysis()

    if (this.current === this.stepTargets.length - 1) return this.decide("accept", event)

    this.goTo(this.current + 1)
  }

  previous(event) {
    event?.preventDefault()
    if (this.decision) return

    // The working beat is not a screen to come back to: it is a wait that already happened.
    const target = this.stepTargets[this.current - 1]
    if (target?.dataset.stepKey === "analyzing") return this.goTo(this.current - 2)

    this.goTo(this.current - 1)
  }

  goTo(index) {
    const bounded = Math.max(0, Math.min(this.stepTargets.length - 1, index))
    if (bounded === this.current) return

    const direction = bounded > this.current ? 1 : -1
    this.current = bounded
    this.showCurrent(direction)
  }

  showCurrent(direction = 0) {
    this.screenAnimation?.cancel()
    this.screenAnimation = null
    this.decisionErrorTarget.classList.add("hidden")
    this.stepTargets.forEach((step, index) => {
      step.classList.toggle("hidden", index !== this.current)
      step.setAttribute("aria-hidden", index === this.current ? "false" : "true")
    })

    this.updateBackButton()
    const section = this.stepTargets[this.current]
    this.trackStep(section?.dataset.stepKey)

    if (direction && !this.reduceMotion) {
      // Keep the footer anchored while the scrollable content slides into place.
      this.screenAnimation = section.firstElementChild.animate([
        { transform: `translate3d(${direction * 100}%, 0, 0)` },
        { transform: "translate3d(0, 0, 0)" }
      ], { duration: this.SCREEN_SLIDE_MS, easing: "cubic-bezier(0.25, 0.1, 0.25, 1)" })
    }
    this.revealScreen(section, this.screenAnimation)

    if (this.currentKey === "analyzing") {
      this.startBeat()
    } else {
      this.analysisAnimation?.cancel()
    }
    if (this.currentKey === "complete" && !this.celebrated) {
      this.celebrated = true
      window.triggerHaptic?.()
      const badge = this.stepTargets[this.current].querySelector("[data-first-plan-celebration]")
      if (badge && !this.reduceMotion) {
        badge.animate([
          { transform: "scale(0.7)", opacity: 0 },
          { transform: "scale(1.12)", opacity: 1, offset: 0.65 },
          { transform: "scale(1)", opacity: 1 }
        ], { duration: 550, easing: "ease-out" })
      }
    }
  }

  // ---- screen entry -------------------------------------------------------------------------

  // A screen animates itself in the first time it is reached: the coach bubble is there from the
  // first frame, and the content blocks follow once there has been time to read it. Coming back to
  // a screen shows it as it was left, whole and at once, because the bubble has already been read.
  revealScreen(section, slide = null) {
    this.cancelReveal()
    if (!section) return

    const key = section.dataset.stepKey
    // The working beat animates itself, and its own count is the thing being waited on.
    if (key === "analyzing") return

    const bubbles = Array.from(section.querySelectorAll("[data-first-plan-bubble]"))
    const continuations = bubbles.slice(1)
    const blocks = Array.from(section.querySelectorAll("[data-first-plan-reveal]"))
    const reveals = [...continuations, ...blocks]
    if (!reveals.length) return

    const seen = this.revealed.has(key)
    this.revealed.add(key)
    if (seen || this.reduceMotion) return this.completeReveal(reveals)

    const token = this.revealToken
    this.animating = { blocks: reveals }
    reveals.forEach((block) => block.classList.add("fade-in-hidden"))

    // Hide immediately, then begin the reading pause and reveals once the slide has settled.
    const startReveals = () => {
      if (token !== this.revealToken) return

      if (continuations.length) {
        this.revealTimeouts.push(setTimeout(() => {
          if (token !== this.revealToken) return

          continuations.forEach((bubble) => {
            bubble.classList.remove("fade-in-hidden")
            bubble.classList.add("fade-in-visible")
          })
        }, this.CONTINUATION_DELAY_MS))
      }

      this.readTimeout = setTimeout(() => {
        if (token === this.revealToken) this.revealBlocks(blocks, token)
      }, bubbles.length ? this.READ_PAUSE_MS : 0)
    }

    if (slide) {
      // Cancellation rejects finished; navigation and disconnect already complete the old screen.
      slide.finished.then(startReveals, () => {})
    } else {
      startReveals()
    }
  }

  revealBlocks(blocks, token) {
    blocks.forEach((block, index) => {
      this.revealTimeouts.push(setTimeout(() => {
        if (token !== this.revealToken) return

        block.classList.remove("fade-in-hidden")
        block.classList.add("fade-in-visible")
        this.animateCompatibility(block)
        if (index === blocks.length - 1) this.animating = null
      }, index * this.REVEAL_STEP_MS))
    })
  }

  // The screen as it ends up: every block in place, nothing held back.
  completeReveal(blocks) {
    blocks.forEach((block) => block.classList.remove("fade-in-hidden", "fade-in-visible"))
  }

  animateCompatibility(block) {
    const indicator = block.querySelector("[data-first-plan-compatibility]")
    if (!indicator || this.reduceMotion) return

    const spinner = indicator.querySelector("[data-first-plan-compatibility-spinner]")
    const check = indicator.querySelector("[data-first-plan-compatibility-check]")
    const message = block.querySelector("[data-first-plan-compatibility-message]")
    this.compatibilityMessage = { element: message, text: message.textContent }
    message.textContent = "Checking for conflicts…"
    this.compatibilityTimeout = setTimeout(() => this.completeCompatibilityMessage(), 3000)

    this.compatibilityAnimations.push(
      spinner.animate([
        { transform: "rotate(0deg)", opacity: 1 },
        { transform: "rotate(1440deg)", opacity: 1, offset: 3000 / 3150 },
        { transform: "rotate(1512deg)", opacity: 0 }
      ], { duration: 3150, easing: "linear" }),
      check.animate([
        { transform: "scale(0)", opacity: 0 },
        { transform: "scale(1.22)", opacity: 1, offset: 0.55 },
        { transform: "scale(0.92)", opacity: 1, offset: 0.78 },
        { transform: "scale(1)", opacity: 1 }
      ], { delay: 3000, duration: 550, easing: "ease-out", fill: "backwards" })
    )
  }

  completeCompatibilityMessage() {
    clearTimeout(this.compatibilityTimeout)
    if (!this.compatibilityMessage) return

    const { element, text } = this.compatibilityMessage
    element.textContent = text
    this.compatibilityMessage = null
  }

  // Navigating away mid-reveal abandons the animation, and the screen it was running on is left
  // finished, so coming back to it shows the whole thing.
  cancelReveal() {
    this.completeCompatibilityMessage()
    this.compatibilityAnimations.forEach((animation) => animation.cancel())
    this.compatibilityAnimations = []
    this.revealToken += 1
    clearTimeout(this.readTimeout)
    this.revealTimeouts.forEach((timeout) => clearTimeout(timeout))
    this.revealTimeouts = []

    const pending = this.animating
    this.animating = null
    if (pending) this.completeReveal(pending.blocks)
  }

  updateBackButton() {
    if (this.hasBackButtonTarget) {
      const canGoBack = this.current > 0 && this.currentKey !== "analyzing" && this.currentKey !== "complete"
      this.backButtonTarget.classList.toggle("hidden", !canGoBack)
    }
  }

  get currentKey() {
    return this.stepTargets[this.current]?.dataset.stepKey
  }

  frequencyChanged(event) {
    const form = event.currentTarget
    form.dataset.edited = "true"
    const select = form.querySelector("select")
    // The cadence sits in the summary as the control's own value, so only that span is rewritten:
    // replacing the summary would take the timing line and the chevron with it.
    const cadence = form.closest("details")?.querySelector("[data-first-plan-cadence]")
    if (cadence && select) {
      const label = select.selectedOptions[0].textContent.trim()
      cadence.textContent = label.charAt(0).toUpperCase() + label.slice(1)
    }
  }

  preventFrequencySubmit(event) {
    event.preventDefault()
  }

  frequencyEdits() {
    return this.frequencyFormTargets.filter((form) => form.dataset.edited === "true").reduce((edits, form) => {
      const fields = new FormData(form)
      edits[form.dataset.routineProductId] = {
        frequency: fields.get("routine_product[frequency]"),
        days: fields.getAll("routine_product[frequency_days_attributes][day][]"),
        rotation_partner_ids: fields.getAll("routine_product[rotation_partner_ids][]"),
        rotation_shared_ids: fields.getAll("routine_product[rotation_shared_ids][]")
      }
      return edits
    }, {})
  }

  // Show the beat immediately and let it run while the plan is built.
  beginAnalysis() {
    this.analysis = this.post(this.assessmentUrlValue, { frequency_edits: this.frequencyEdits() }, "text")
      .then((html) => {
        this.analysisSucceeded = html !== null
        if (this.analysisSucceeded) {
          this.computedTarget.innerHTML = html
          this.frequencyFormTargets.forEach((form) => {
            this.application.getControllerForElementAndIdentifier(form, "bridge--navigation-guard")?.deactivate()
          })
        }
      })

    this.goToKey("analyzing")
  }

  // ---- working beat -------------------------------------------------------------------------

  startBeat() {
    this.analysisAnimation?.cancel()
    const animation = new FirstPlanAnalysisAnimation({
      ring: this.hasRingTarget ? this.ringTarget : null,
      percent: this.hasPercentTarget ? this.percentTarget : null,
      status: this.hasStatusTarget ? this.statusTarget : null,
      carriers: this.carrierTargets,
      ingredientCount: this.ingredientCountValue,
      pairingCount: this.pairingCountValue,
      reduceMotion: this.reduceMotion,
      onComplete: () => this.showResults()
    })
    this.analysisAnimation = animation
    animation.start()
    this.analysis.finally(() => animation.finish(this.analysisSucceeded))
  }

  showResults() {
    if (!this.analysisSucceeded) {
      this.goToKey("match_scores")
      this.decisionErrorTarget.classList.remove("hidden")
      return
    }

    const firstStep = this.computedTarget.querySelector("[data-step-key]")
    if (firstStep) return this.goToKey(firstStep.dataset.stepKey)

    return this.decide("accept")
  }

  // ---- decisions ----------------------------------------------------------------------------

  chooseProduct(event) {
    if (this.decision) return

    this.productChoice = event.currentTarget.dataset.choice
    this.productChoiceId = event.currentTarget.dataset.productId
    if (this.productChoice === "compare") return this.decide("accept", event)

    this.next(event)
  }

  dismiss() {
    this.post(this.decisionUrlValue, { decision: "dismiss" }).finally(() => this.leave())
  }

  // Render the final routine from the saved decision, including when there are no changes.
  // Keep the current screen available for retry if the request fails.
  async decide(decision, event) {
    if (this.decision) return

    const button = event?.currentTarget
    const loading = button && this.application.getControllerForElementAndIdentifier(button, "loading-button")
    const buttons = this.stepTargets[this.current].querySelectorAll("button")
    this.decisionErrorTarget.classList.add("hidden")
    loading?.showLoading()
    buttons.forEach((element) => { element.disabled = true })

    this.decision = this.post(this.decisionUrlValue, { decision: decision, product_id: this.productChoiceId, product_choice: this.productChoice === "later" ? null : this.productChoice }, "text")
    try {
      const html = await this.decision
      if (html) {
        if (this.productChoice === "compare") {
          window.Turbo.visit(this.nextStepUrlValue)
          return
        }
        this.productChoice = null
        this.completionTarget.innerHTML = html
        this.goToKey("complete")
      } else {
        if (this.currentKey === "analyzing") this.goToKey("intro")
        this.decisionErrorTarget.classList.remove("hidden")
      }
    } finally {
      this.decision = null
      loading?.restoreButton()
      buttons.forEach((element) => { element.disabled = false })
    }
  }

  startRoutine() {
    this.trackStep("complete")
    window.Turbo.visit(`${this.todayUrlValue}#todays-routine`)
  }

  async saveReminder(event) {
    if (this.savingReminder) return
    this.savingReminder = true
    const button = event.currentTarget
    const loading = this.application.getControllerForElementAndIdentifier(button, "loading-button")
    loading?.showLoading()
    try {
      const response = await fetch(this.reminderUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
        body: JSON.stringify({ reminder_time: button.dataset.reminderTime })
      })
      if (!response.ok) throw new Error("Reminder failed")
      this.reminderStatusTarget.textContent = `Reminder set for ${button.textContent.trim()}.`
      this.reminderDoneTarget.classList.remove("hidden")
    } catch {
      this.reminderStatusTarget.textContent = "We couldn’t set your reminder. Please try again."
    } finally {
      this.savingReminder = false
      loading?.restoreButton()
    }
  }

  finish() {
    this.trackStep("complete")
    this.leave()
  }

  // On native the flow is a modal over the tab bar, so dismissing it and refreshing what is
  // underneath is the hand-off. On web there is no modal, so go to the page itself.
  leave() {
    const destination = this.isNativeApp ? "/refresh_historical_location" : this.todayUrlValue

    if (window.Turbo?.visit) {
      window.Turbo.visit(destination)
    } else {
      window.location.href = destination
    }
  }

  get isNativeApp() {
    return document.documentElement.hasAttribute("data-turbo-native-app")
  }

  // ---- plumbing -----------------------------------------------------------------------------

  indexOf(key) {
    return this.stepTargets.findIndex((step) => step.dataset.stepKey === key)
  }

  // Screens injected after the page loaded are not Stimulus targets until the observer has run, so
  // asking for one by name right after writing it in returns nothing. Wait a frame and ask again
  // rather than navigating to an index that does not exist yet, which would land on screen one.
  goToKey(key, attempts = 0) {
    const index = this.indexOf(key)
    if (index !== -1) return this.goTo(index)
    if (attempts > 30) return this.leave()

    requestAnimationFrame(() => this.goToKey(key, attempts + 1))
  }

  // A screen reports itself on arrival rather than on the tap that leaves it. The continue button
  // is only one of the ways out -- skipping, the choice forms that post to the next step, and
  // closing the modal all leave without it -- so departure events only ever described the people
  // who were already finishing. Once per screen per page load: coming back to a screen is not a
  // new step reached.
  trackStep(stepKey) {
    if (!stepKey || this.trackedSteps.has(stepKey)) return

    this.trackedSteps.add(stepKey)
    this.post(this.analyticsUrlValue, { step_key: stepKey }, "none", true)
  }

  post(url, body, expect = "none", keepalive = false) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
      body: JSON.stringify(body),
      credentials: "same-origin",
      keepalive: keepalive
    })
      .then((response) => (expect === "text" && response.ok ? response.text() : null))
      .catch(() => null)
  }
}
